'use client'

import { keyframes } from '@emotion/react'
import { LuneTheme } from '@lune-fe/lune-ui-lib'
import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image, { type StaticImageData } from 'next/image'
import NextLink from 'next/link'
import {
    type KeyboardEvent,
    type Ref,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react'

const { palette } = LuneTheme
const ADVANCE_MS = 6000
const DESKTOP_QUERY = '(min-width: 900px)'
const REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const CARD_IMAGE_SIZES = 'calc(86vw - 32px)'
const ANSWER_IMAGE_SIZES = `(min-width: 900px) min(880px, max(460px, calc((100vh - 290px) * 1744 / 1280))), ${CARD_IMAGE_SIZES}`
const IMAGE_QUALITY = 90

const fadeUp = (distance: string) => keyframes`
    from {
        opacity: 0;
        transform: translateY(${distance});
    }
    to {
        opacity: 1;
        transform: none;
    }
`

const progressFill = keyframes`
    from {
        height: 0;
    }
    to {
        height: 100%;
    }
`

const changeMotion = {
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
    },
} as const

const focusRing = (offset: string) => ({
    '&:focus': {
        outline: 'none',
    },
    '&:focus-visible': {
        outline: `2px solid ${palette.Blue600}`,
        outlineOffset: offset,
    },
})

export type RequestsShowcaseImage = {
    src: StaticImageData | string
    alt: string
}

export type RequestsShowcaseProduct = {
    name: string
    icon: StaticImageData | string
    href: string
}

export type RequestsShowcaseItem = {
    request: string
    image: RequestsShowcaseImage
    product: RequestsShowcaseProduct
    description: string
}

export type RequestsShowcaseProps = {
    heading: string
    lead?: string
    items: RequestsShowcaseItem[]
    label?: string
    quoted?: boolean
    centerHeading?: boolean
}

function useMediaQuery(query: string, serverSnapshot: boolean) {
    return useSyncExternalStore(
        (onChange) => {
            const media = window.matchMedia(query)
            media.addEventListener('change', onChange)
            return () => media.removeEventListener('change', onChange)
        },
        () => window.matchMedia(query).matches,
        () => serverSnapshot,
    )
}

function imageDimensions(src: StaticImageData | string) {
    if (typeof src === 'string') {
        return { width: 1744, height: 1280 }
    }
    return { width: src.width, height: src.height }
}

function openingQuoteHangs(request: string) {
    return request.startsWith('"') || request.startsWith('“')
}

const RequestsShowcase = ({
    heading,
    lead,
    items,
    label = 'Customer requests',
    quoted = true,
    centerHeading = false,
}: RequestsShowcaseProps) => {
    const baseId = useId()
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const listRef = useRef<HTMLDivElement>(null)
    const panelsRef = useRef<HTMLDivElement>(null)
    const imageFrameRef = useRef<HTMLDivElement>(null)
    const [selected, setSelected] = useState(0)
    const [activation, setActivation] = useState(0)
    const [userStopped, setUserStopped] = useState(false)
    const prefersReducedMotion = useMediaQuery(REDUCE_MOTION_QUERY, false)
    const canAutoAdvance = useMediaQuery(DESKTOP_QUERY, false) && !prefersReducedMotion
    const playing = canAutoAdvance && !userStopped

    useEffect(() => {
        if (!playing || items.length === 0) {
            return undefined
        }
        const timer = window.setTimeout(() => {
            setSelected((current) => (current + 1) % items.length)
            setActivation((current) => current + 1)
        }, ADVANCE_MS)
        return () => window.clearTimeout(timer)
    }, [playing, selected, items.length])

    const alignList = useCallback(() => {
        const list = listRef.current
        const panels = panelsRef.current
        const image = imageFrameRef.current
        if (!list || !panels) {
            return
        }
        const desktop = window.matchMedia(DESKTOP_QUERY).matches
        const difference = desktop && image ? (image.offsetHeight - list.offsetHeight) / 2 : 0
        list.style.marginTop = difference > 0 ? `${difference}px` : '0px'
        panels.style.marginTop = difference < 0 ? `${-difference}px` : '0px'
    }, [])

    useLayoutEffect(() => {
        const list = listRef.current
        alignList()
        if (!list) {
            return undefined
        }
        const observer = new ResizeObserver(alignList)
        observer.observe(list)
        if (imageFrameRef.current) {
            observer.observe(imageFrameRef.current)
        }
        return () => observer.disconnect()
    }, [alignList, selected, activation])

    if (items.length === 0) {
        return null
    }

    const selectedIndex = selected % items.length
    const nextIndex = (selectedIndex + 1) % items.length
    const answer = items[selectedIndex]
    const panelId = `${baseId}-panel`

    const select = (index: number, fromUser: boolean) => {
        if (fromUser) {
            setUserStopped(true)
        }
        setSelected(index)
        setActivation((current) => current + 1)
    }

    const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key]
        const next =
            step !== undefined
                ? (selectedIndex + step + items.length) % items.length
                : event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? items.length - 1
                    : null
        if (next === null) {
            return
        }
        event.preventDefault()
        select(next, true)
        tabRefs.current[next]?.focus()
    }

    return (
        <Box
            component="section"
            sx={{
                position: 'relative',
                width: { xs: '100vw', md: 'auto' },
                marginLeft: { xs: 'calc(50% - 50vw)', md: 0 },
            }}
        >
            <Box
                sx={
                    centerHeading
                        ? {
                              display: 'flex',
                              alignItems: { xs: 'flex-start', md: 'center' },
                              flexDirection: 'column',
                          }
                        : undefined
                }
            >
                <Text
                    component="h2"
                    variant="h4"
                    sx={{
                        m: 0,
                        mb: lead === undefined ? { xs: '48px', md: 6 } : 3,
                        maxWidth: centerHeading ? 'none' : '760px',
                        scrollMarginTop: '120px',
                        typography: { xs: 'h5', md: 'h4' },
                        px: { xs: '20px', md: 0 },
                        ...(centerHeading ? { textAlign: { xs: 'left', md: 'center' } } : {}),
                    }}
                >
                    {heading}
                </Text>
            </Box>
            {lead === undefined ? null : (
                <Text
                    component="p"
                    variant="body1"
                    sx={{
                        m: 0,
                        mb: { xs: '28px', md: 6 },
                        maxWidth: '600px',
                        px: { xs: '20px', md: 0 },
                    }}
                >
                    {lead}
                </Text>
            )}
            <MobileCards items={items} label={label} quoted={quoted} />
            <Box
                sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: '360px minmax(0, 1fr)',
                    columnGap: '56px',
                    alignItems: 'start',
                }}
            >
                <Box
                    ref={listRef}
                    role="tablist"
                    aria-label={label}
                    aria-orientation="vertical"
                    onKeyDown={onListKeyDown}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                    }}
                >
                    {items.map((item, index) => {
                        const isSelected = index === selectedIndex
                        const tabId = `${baseId}-tab-${index}`
                        return (
                            <Box
                                key={item.request}
                                component="button"
                                type="button"
                                role="tab"
                                id={tabId}
                                aria-controls={panelId}
                                aria-selected={isSelected}
                                tabIndex={isSelected ? 0 : -1}
                                ref={(node: HTMLButtonElement | null) => {
                                    tabRefs.current[index] = node
                                }}
                                onClick={() => select(index, true)}
                                sx={{
                                    margin: 0,
                                    padding: '22px 0 22px 28px',
                                    border: 0,
                                    borderRadius: 0,
                                    background: 'transparent',
                                    boxShadow: 'none',
                                    font: 'inherit',
                                    textAlign: 'left',
                                    width: '100%',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    display: 'block',
                                    boxSizing: 'border-box',
                                    color: isSelected ? palette.Grey900 : palette.Grey500,
                                    transition: 'color .25s',
                                    '&:hover': {
                                        color: palette.Grey900,
                                    },
                                    ...focusRing('4px'),
                                }}
                            >
                                <RequestQuote
                                    request={item.request}
                                    quoted={quoted}
                                    variant="body1"
                                    sx={{ color: 'inherit' }}
                                />
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: '2px',
                                        backgroundColor: palette.Grey250,
                                        '@media (prefers-reduced-motion: reduce)': {
                                            display: 'none',
                                        },
                                    }}
                                >
                                    <Box
                                        key={isSelected && playing ? `run-${activation}` : 'rest'}
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            width: '100%',
                                            height: isSelected && !playing ? '100%' : 0,
                                            backgroundColor: palette.Blue600,
                                            ...(isSelected && playing
                                                ? {
                                                      animation: `${progressFill} ${ADVANCE_MS}ms linear forwards`,
                                                  }
                                                : {}),
                                        }}
                                    />
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
                <Box
                    ref={panelsRef}
                    role="tabpanel"
                    id={panelId}
                    aria-labelledby={`${baseId}-tab-${selectedIndex}`}
                    sx={{
                        minWidth: 0,
                        justifySelf: 'end',
                        width: 'min(100%, max(460px, calc((100vh - 290px) * 1744 / 1280)))',
                    }}
                >
                    <Answer
                        key={`${selectedIndex}-${activation}`}
                        item={answer}
                        priority={selectedIndex === 0}
                        imageFrameRef={imageFrameRef}
                        onImageLoad={alignList}
                    />
                </Box>
            </Box>
            {items.length > 1 ? (
                <Box
                    aria-hidden
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                        clipPath: 'inset(50%)',
                    }}
                >
                    <ShowcaseImage
                        image={items[nextIndex].image}
                        sizes={ANSWER_IMAGE_SIZES}
                        alt=""
                        eager
                    />
                </Box>
            ) : null}
        </Box>
    )
}

function MobileCards({
    items,
    label,
    quoted,
}: {
    items: RequestsShowcaseItem[]
    label: string
    quoted: boolean
}) {
    const trackRef = useRef<HTMLDivElement>(null)
    const cardRefs = useRef<Array<HTMLElement | null>>([])
    const [active, setActive] = useState(0)
    const [shown, setShown] = useState<boolean[]>(() => items.map((_, index) => index < 2))

    const syncFromScroll = useCallback(() => {
        const track = trackRef.current
        if (!track || track.getClientRects().length === 0) {
            return
        }
        const trackRect = track.getBoundingClientRect()
        let best = 0
        let bestDistance = Number.POSITIVE_INFINITY
        cardRefs.current.forEach((card, index) => {
            if (!card) {
                return
            }
            const distance = Math.abs(card.getBoundingClientRect().left - trackRect.left - 20)
            if (distance < bestDistance) {
                bestDistance = distance
                best = index
            }
        })
        setActive(best)
    }, [])

    useEffect(() => {
        const track = trackRef.current
        if (!track) {
            return undefined
        }
        syncFromScroll()
        track.addEventListener('scroll', syncFromScroll, { passive: true })
        const resizeObserver = new ResizeObserver(syncFromScroll)
        resizeObserver.observe(track)
        const observer = new IntersectionObserver(
            (entries) => {
                setShown((current) => {
                    const next = current.slice()
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) {
                            return
                        }
                        const index = Number(entry.target.getAttribute('data-index'))
                        if (index >= 0 && index < next.length && !next[index]) {
                            next[index] = true
                        }
                    })
                    return next.some((value, index) => value !== current[index]) ? next : current
                })
            },
            { root: track, rootMargin: '0px 40% 0px 40%', threshold: 0.01 },
        )
        cardRefs.current.forEach((card) => {
            if (card) {
                observer.observe(card)
            }
        })
        return () => {
            observer.disconnect()
            resizeObserver.disconnect()
            track.removeEventListener('scroll', syncFromScroll)
        }
    }, [syncFromScroll])

    const goTo = (index: number) => {
        const track = trackRef.current
        const card = cardRefs.current[index]
        if (!track || !card) {
            return
        }
        const reduce = window.matchMedia(REDUCE_MOTION_QUERY).matches
        track.scrollTo({
            left: card.offsetLeft - 20,
            behavior: reduce ? 'auto' : 'smooth',
        })
    }

    return (
        <Box role="region" aria-label={label} sx={{ display: { xs: 'block', md: 'none' } }}>
            <Box
                ref={trackRef}
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: '12px',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    pb: '4px',
                    scrollPaddingLeft: '20px',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                }}
            >
                {items.map((item, index) => (
                    <Box
                        key={item.request}
                        component="article"
                        aria-roledescription="slide"
                        aria-label={`${index + 1} of ${items.length}`}
                        data-index={index}
                        ref={(node: HTMLElement | null) => {
                            cardRefs.current[index] = node
                        }}
                        sx={{
                            flex: '0 0 86%',
                            scrollSnapAlign: 'start',
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            backgroundColor: palette.White,
                            border: `1px solid ${palette.Grey250}`,
                            borderRadius: '20px',
                            padding: '18px 16px 20px',
                            '&:first-of-type': {
                                ml: '20px',
                            },
                            '&:last-of-type': {
                                mr: 'calc(14% - 20px)',
                            },
                        }}
                    >
                        <RequestQuote
                            request={item.request}
                            quoted={quoted}
                            component="p"
                            sx={{
                                m: '2px 0 16px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                fontWeight: 700,
                                color: palette.Grey900,
                            }}
                        />
                        <Box
                            sx={{
                                borderRadius: '14px',
                                overflow: 'hidden',
                                backgroundColor: palette.Blue50,
                                lineHeight: 0,
                                aspectRatio: shown[index] ? undefined : '1744 / 1280',
                            }}
                        >
                            {shown[index] ? (
                                <ShowcaseImage
                                    image={item.image}
                                    sizes={CARD_IMAGE_SIZES}
                                    priority={index === 0}
                                    unoptimized
                                    eager
                                />
                            ) : null}
                        </Box>
                        <AnswerCaption item={item} />
                    </Box>
                ))}
            </Box>
            <Box
                role="group"
                aria-label={`Choose ${label}`}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    mt: '18px',
                }}
            >
                {items.map((item, index) => {
                    const isCurrent = index === active
                    return (
                        <Box
                            key={item.request}
                            component="button"
                            type="button"
                            aria-label={`Go to ${index + 1}`}
                            aria-current={isCurrent ? 'true' : undefined}
                            onClick={() => goTo(index)}
                            sx={{
                                margin: 0,
                                padding: 0,
                                border: 0,
                                width: isCurrent ? '20px' : '8px',
                                height: '8px',
                                borderRadius: isCurrent ? '4px' : '50%',
                                backgroundColor: isCurrent ? palette.Blue600 : palette.Grey300,
                                cursor: 'pointer',
                                ...focusRing('3px'),
                            }}
                        />
                    )
                })}
            </Box>
        </Box>
    )
}

function displayRequest(request: string, quoted: boolean) {
    if (quoted) {
        return request
    }
    return request.replace(/^[“"]/, '').replace(/[”"]$/, '')
}

function RequestQuote({
    request,
    quoted,
    component = 'span',
    variant,
    sx,
}: {
    request: string
    quoted: boolean
    component?: 'span' | 'p'
    variant?: 'body1'
    sx?: SxProps
}) {
    return (
        <Text
            component={component}
            variant={variant}
            sx={[
                {
                    display: 'block',
                    textIndent: quoted && openingQuoteHangs(request) ? '-0.42em' : 0,
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {displayRequest(request, quoted)}
        </Text>
    )
}

function ShowcaseImage({
    image,
    sizes,
    priority = false,
    unoptimized = false,
    eager = false,
    alt,
    onLoad,
}: {
    image: RequestsShowcaseImage
    sizes: string
    priority?: boolean
    unoptimized?: boolean
    eager?: boolean
    alt?: string
    onLoad?: () => void
}) {
    const { width, height } = imageDimensions(image.src)
    return (
        <Image
            src={image.src}
            alt={alt ?? image.alt}
            width={width}
            height={height}
            sizes={sizes}
            quality={IMAGE_QUALITY}
            priority={priority}
            unoptimized={unoptimized}
            loading={priority || !eager ? undefined : 'eager'}
            onLoad={onLoad}
            style={{ width: '100%', height: 'auto', display: 'block' }}
        />
    )
}

function LearnMore({ href }: { href: string }) {
    return (
        <Text
            component={NextLink}
            href={href}
            variant="button"
            sx={{
                gridColumn: { xs: 1, md: 2 },
                gridRow: { xs: 3, md: 1 },
                justifySelf: { xs: 'start', md: 'end' },
                alignSelf: 'center',
                mt: { xs: '8px', md: 0 },
                color: palette.Grey900,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                    textDecoration: 'underline',
                },
                ...focusRing('2px'),
            }}
        >
            Learn more{' '}
            <Box component="span" aria-hidden>
                →
            </Box>
        </Text>
    )
}

function AnswerCaption({ item }: { item: RequestsShowcaseItem }) {
    return (
        <Box
            sx={{
                mt: { xs: '16px', md: 0 },
                flex: 1,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
                gridTemplateRows: { xs: 'auto 1fr auto', md: 'auto auto' },
                columnGap: { md: 2 },
                rowGap: { xs: '6px', md: 1 },
                alignItems: { md: 'center' },
            }}
        >
            <Box
                sx={{
                    gridColumn: 1,
                    gridRow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: '8px', md: '10px' },
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: { xs: 24, md: 32 },
                        height: { xs: 24, md: 32 },
                        flex: 'none',
                        lineHeight: 0,
                    }}
                >
                    <Image
                        src={item.product.icon}
                        alt=""
                        width={32}
                        height={32}
                        style={{ width: '100%', height: '100%', display: 'block' }}
                    />
                </Box>
                <Text
                    component="span"
                    variant="h6"
                    sx={{
                        m: 0,
                        fontSize: { xs: '15px', md: '18px' },
                        lineHeight: { xs: '20px', md: '22px' },
                        fontWeight: { xs: 600, md: 700 },
                    }}
                >
                    {item.product.name}
                </Text>
            </Box>
            <Text
                component="p"
                variant="body2"
                sx={{
                    m: 0,
                    gridColumn: { xs: 1, md: '1 / -1' },
                    gridRow: 2,
                    alignSelf: 'start',
                    ml: { md: '42px' },
                    maxWidth: { md: '620px' },
                    fontSize: { xs: '15px', md: '16px' },
                    lineHeight: { xs: '20px', md: '19.2px' },
                    color: { xs: palette.Grey700 },
                }}
            >
                {item.description}
            </Text>
            <LearnMore href={item.product.href} />
        </Box>
    )
}

function Answer({
    item,
    priority,
    imageFrameRef,
    onImageLoad,
}: {
    item: RequestsShowcaseItem
    priority: boolean
    imageFrameRef?: Ref<HTMLDivElement>
    onImageLoad?: () => void
}) {
    return (
        <>
            <Box
                ref={imageFrameRef}
                sx={{
                    borderRadius: '24px',
                    overflow: 'hidden',
                    backgroundColor: palette.Blue50,
                    lineHeight: 0,
                }}
            >
                <Box
                    sx={{
                        animation: `${fadeUp('14px')} 600ms cubic-bezier(0.2, 0.75, 0.2, 1) both`,
                        ...changeMotion,
                    }}
                >
                    <ShowcaseImage
                        image={item.image}
                        sizes={ANSWER_IMAGE_SIZES}
                        priority={priority}
                        onLoad={onImageLoad}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    mt: '20px',
                    animation: `${fadeUp('8px')} 500ms cubic-bezier(0.2, 0.75, 0.2, 1) 250ms both`,
                    ...changeMotion,
                }}
            >
                <AnswerCaption item={item} />
            </Box>
        </>
    )
}

export default RequestsShowcase
